/**
 * Import Processor
 * Handles batch processing of Twitter data export files
 * Coordinates parsing, validation, and batch uploading to backend
 */

import type {
  ImportStats,
} from '@/types/api';
import {
  validateTwitterExport,
  extractTables,
  parseCreatedAt,
  extractMediaUrls,
  buildCapturesMap,
  shouldImportTweet,
  type TwitterExportData,
  type TweetData,
  type CaptureData,
  type UserData,
} from './import-parser';

// API import will be added dynamically to avoid circular dependency
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

async function handleResponse(response: Response): Promise<any> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
}

// Dynamic API_BASE
declare const __API_BASE__: string;
const API_BASE = typeof __API_BASE__ !== 'undefined' ? __API_BASE__ : '/api';

/**
 * Progress callback during import
 */
export interface ImportProgress {
  stage: 'parsing' | 'uploading' | 'completed' | 'error';
  current: number;
  total: number;
  progress: number;
  stats: ImportStats;
  error?: string;
}

export type ProgressCallback = (progress: ImportProgress) => void;

/**
 * Transform TweetData to database format
 */
function transformTweet(tweet: TweetData): {
  restId: string;
  fullText: string;
  createdAt: number | null;
  favoriteCount: number;
  retweetCount: number;
  bookmarkCount: number;
  quoteCount: number;
  replyCount: number;
  favorited: number;
  bookmarked: number;
  screenName: string;
  userName: string;
  userAvatarUrl: string;
  mediaUrls: string | null;
  hasMedia: number;
  lang: string;
  isQuote: number;
  source: string;
} {
  const legacy = tweet.legacy || {};
  const user = tweet.core?.user_results?.result || {};
  const userCore = user.core || {};
  const userLegacy = user.legacy || {};
  const mediaUrls = extractMediaUrls(tweet);

  return {
    restId: tweet.rest_id,
    fullText: legacy.full_text || '',
    createdAt: parseCreatedAt(legacy.created_at),
    favoriteCount: legacy.favorite_count || 0,
    retweetCount: legacy.retweet_count || 0,
    bookmarkCount: legacy.bookmark_count || 0,
    quoteCount: legacy.quote_count || 0,
    replyCount: legacy.reply_count || 0,
    favorited: legacy.favorited ? 1 : 0,
    bookmarked: legacy.bookmarked ? 1 : 0,
    screenName: userCore.screen_name || '',
    userName: userCore.name || userLegacy.name || '',
    userAvatarUrl: user.avatar?.image_url || userLegacy.profile_image_url_https || '',
    mediaUrls,
    hasMedia: mediaUrls ? 1 : 0,
    lang: legacy.lang || '',
    isQuote: legacy.is_quote_status ? 1 : 0,
    source: legacy.source || ''
  };
}

/**
 * Transform CaptureData to database format
 */
function transformCapture(capture: CaptureData): {
  id: string;
  extension: string;
  type: string;
  dataKey: string;
  createdAt: number | null;
} {
  return {
    id: capture.id,
    extension: capture.extension,
    type: capture.type,
    dataKey: capture.data_key,
    createdAt: capture.created_at || null
  };
}

/**
 * Transform UserData to database format
 */
function transformUser(user: UserData): {
  restId: string;
  screenName: string;
  name: string;
  description: string;
  followersCount: number;
  friendsCount: number;
  favouritesCount: number;
  statusesCount: number;
  avatarUrl: string;
} {
  const legacy = user.legacy || {};
  return {
    restId: user.rest_id,
    screenName: user.core?.screen_name || '',
    name: user.core?.name || legacy.name || '',
    description: legacy.description || '',
    followersCount: legacy.followers_count || 0,
    friendsCount: legacy.friends_count || 0,
    favouritesCount: legacy.favourites_count || 0,
    statusesCount: legacy.statuses_count || 0,
    avatarUrl: user.avatar?.image_url || legacy.profile_image_url_https || ''
  };
}

/**
 * Upload a batch of data to the backend
 */
async function uploadBatch(
  type: 'captures' | 'users' | 'tweets',
  batch: any[]
): Promise<{ inserted: number; skipped: number }> {
  const response = await fetch(`${API_BASE}/import/batch`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ type, batch }),
  });
  const result = await handleResponse(response);
  return {
    inserted: result.count || 0,
    skipped: result.skipped || 0
  };
}

/**
 * Import Processor Class
 */
export class ImportProcessor {
  private readonly BATCH_SIZE = 50;

  /**
   * Main import function - parses file and uploads in batches
   */
  async processImport(
    file: File,
    onProgress: ProgressCallback
  ): Promise<ImportStats> {
    const stats: ImportStats = { tweets: 0, captures: 0, users: 0, duplicates: 0 };

    try {
      // Stage 1: Parse JSON file
      onProgress({
        stage: 'parsing',
        current: 0,
        total: 100,
        progress: 0,
        stats: { ...stats }
      });

      const jsonData: TwitterExportData = await this.parseFile(file);

      if (!validateTwitterExport(jsonData)) {
        throw new Error('Invalid Twitter export file. Expected formatName: "dexie"');
      }

      const { tweets, captures, users } = extractTables(jsonData);

      // Stage 2: Build captures map
      const capturesMap = buildCapturesMap(captures);

      // Stage 3: Filter tweets (only those with likes/bookmarks)
      const filteredTweets = tweets.filter(t => shouldImportTweet(t.rest_id, capturesMap));

      // Calculate total items to process
      const totalItems = captures.length + users.length + filteredTweets.length;
      let processedItems = 0;

      // Stage 4: Upload in batches
      onProgress({
        stage: 'uploading',
        current: 0,
        total: totalItems,
        progress: 0,
        stats: { ...stats }
      });

      // Upload captures first
      for (let i = 0; i < captures.length; i += this.BATCH_SIZE) {
        const batch = captures.slice(i, i + this.BATCH_SIZE);
        const transformed = batch.map(transformCapture);
        const { inserted, skipped } = await uploadBatch('captures', transformed);
        stats.captures += inserted;
        stats.duplicates += skipped;
        processedItems += batch.length;

        onProgress({
          stage: 'uploading',
          current: processedItems,
          total: totalItems,
          progress: Math.floor((processedItems / totalItems) * 100),
          stats: { ...stats }
        });
      }

      // Upload users
      for (let i = 0; i < users.length; i += this.BATCH_SIZE) {
        const batch = users.slice(i, i + this.BATCH_SIZE);
        const transformed = batch.map(transformUser);
        const { inserted, skipped } = await uploadBatch('users', transformed);
        stats.users += inserted;
        stats.duplicates += skipped;
        processedItems += batch.length;

        onProgress({
          stage: 'uploading',
          current: processedItems,
          total: totalItems,
          progress: Math.floor((processedItems / totalItems) * 100),
          stats: { ...stats }
        });
      }

      // Upload tweets
      for (let i = 0; i < filteredTweets.length; i += this.BATCH_SIZE) {
        const batch = filteredTweets.slice(i, i + this.BATCH_SIZE);
        const transformed = batch.map(transformTweet);
        const { inserted, skipped } = await uploadBatch('tweets', transformed);
        stats.tweets += inserted;
        stats.duplicates += skipped;
        processedItems += batch.length;

        onProgress({
          stage: 'uploading',
          current: processedItems,
          total: totalItems,
          progress: Math.floor((processedItems / totalItems) * 100),
          stats: { ...stats }
        });
      }

      // Complete
      onProgress({
        stage: 'completed',
        current: totalItems,
        total: totalItems,
        progress: 100,
        stats: { ...stats }
      });

      return stats;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Import failed';
      onProgress({
        stage: 'error',
        current: 0,
        total: 0,
        progress: 0,
        stats: { ...stats },
        error: errorMessage
      });
      throw error;
    }
  }

  /**
   * Parse JSON file
   */
  private async parseFile(file: File): Promise<TwitterExportData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target?.result as string);
          resolve(json);
        } catch (err) {
          reject(new Error('Failed to parse JSON file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }
}
